import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getCategoryState,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Img, { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowRightLongIcon from "@tCentro/Icons/ArrowRightLongIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showImages = "showImages",
    showOnlyTopLevelCategories = "showOnlyTopLevelCategories",
    title = "title",
    description = "description",
    linkTitle = "linkTitle",
    linkURL = "linkURL",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImages]: boolean;
        [fields.showOnlyTopLevelCategories]: boolean;
        [fields.title]: string;
        [fields.description]: string;
        [fields.linkTitle]: string;
        [fields.linkURL]: string;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const getCategoriesParameter = {
        maxDepth: props.fields.showOnlyTopLevelCategories ? 1 : 2,
        includeStartCategory: false,
    };
    return {
        categoriesDataView: getCategoriesDataView(state, getCategoriesParameter),
        getCategoriesParameter,
        topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
    };
};

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    container?: GridContainerProps;
    imageWrapper?: InjectableCss;
    categoryItem?: GridItemProps;
    subCategoryWrapper?: InjectableCss;
    innerContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: ClickablePresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryName?: TypographyProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    viewMoreLinkItem?: GridItemProps;
    viewMoreLink?: LinkPresentationProps;
    containerWrapper?: InjectableCss;
    title?: InjectableCss;
    description?: InjectableCss;
    readMoreLink?: InjectableCss;
    rightIconStyles?: IconPresentationProps;
    titleContainer?: InjectableCss;
    mainContainer?: InjectableCss;
}

export const categoryListStyles: CategoryListStyles = {
    mainContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        background-color: #f2f2f2;
                    `,
                    css`
                        background-color: #f2f2f2;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    titleContainer: {
        css: css`
            display: flex;
            width: 100%;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-bottom: 24px;
                            flex-direction: column;
                            justify-content: center;
                            gap: 16px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    containerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
            text-align: center;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css`
                            padding-left: 16px;
                            padding-right: 16px;
                            padding-top: 32px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    title: {
        css: css`
            text-align: left;
            color: ${getProp("theme.typography.h2.color")};
            margin-bottom: 0px !important;
            font-family: ${TYPOGRAPHY.fontFamily.base} !important ;
            font-size: ${TYPOGRAPHY.size.xxlarge} !important;
            font-weight: ${TYPOGRAPHY.weight.bold} !important;
            line-height: 40px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css`
                            text-align: center;
                        `,
                        css`
                            font-family: ${TYPOGRAPHY.fontFamily.title} !important;
                            font-size: ${TYPOGRAPHY.size.largest} !important;
                            font-weight: ${TYPOGRAPHY.weight.mediumbold} !important;
                            line-height: 32px !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    description: {
        css: css`
            margin-top: 0 !important;
            margin-bottom: 40px !important;
            font-weight: 400;
            color: ${getProp("theme.typography.p.color")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 16px;
                            line-height: 20px;
                            margin-bottom: 24px;
                            margin-bottom: 20px !important;
                            display: flex;
                            flex-direction: column;
                        `,
                        css`
                            font-size: 16px;
                            line-height: 20px;
                            margin-bottom: 24px;
                            margin-bottom: 20px !important;
                            display: flex;
                            flex-direction: column;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    readMoreLink: {
        css: css`
            > span {
                font-family: ${TYPOGRAPHY.fontFamily.title};
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    container: {
        gap: 0,
        css: css`
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 78px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    innerContainer: {
        gap: 24,
        css: css`
            background: ${COLORS.white};
        `,
    },
    categoryItem: {
        width: [12, 6, 4, 4, 3],
        css: css`
            padding: 12px !important;
            margin-top: 20px !important;
            div[class^="GridWrapper"] {
                height: 100%;
                width: 100%;
                background: rgb(242, 242, 242);
                border: 1px solid rgb(242, 242, 242);
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 78px;
                    `,
                    null,
                    null,
                    null,
                ])}
            @media (min-width: 1200px) {
                flex-basis: 25%;
                max-width: 25%;
            }
        `,
    },
    categoryImageItem: {
        width: 12,
        css: css`
            height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            a {
                justify-content: center;
                height: 100%;
                display: flex;
            }
        `,
    },
    categoryImageLink: {
        css: css`
            width: 100%;
            display: inline-flex;
            height: 100%;
        `,
    },
    categoryImage: {
        css: css`
            width: 100%;
            background: ${COLORS.white};
            img {
                width: 100%;
                object-fit: contain;
                height: auto;
            }
        `,
    },
    subCategoryWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 0px 15px;
            padding-bottom: 20px;
        `,
    },
    categoryNameLinkItem: {
        width: 12,
        css: css`
            padding: 30px 32px 56px 32px;
            display: flex;
            justify-content: left;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 32px;
                        background-color: white;
                    `,
                    css`
                        background-color: white;
                        padding: 32px;
                    `,
                    css`
                        padding: 23px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    categoryName: {
        css: css`
            color: ${getProp("theme.typography.p.color")};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.large};
            line-height: 24px;
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            span {
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: 16px;
            }
        `,
    },
    viewMoreLinkItem: {
        width: 12,
        css: css`
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            padding-top: 10px;
        `,
    },
    rightIconStyles: {
        css: css`
            height: 11.25px;
            width: 18px;
            margin-left: 5px;
            svg {
                height: 11.25px;
                width: 18px;
                > path {
                    fill: ${getColor("text.link")};
                }
            }
        `,
    },
};

const styles = categoryListStyles;

class HomepageCategoryList extends React.Component<Props> {
    container = React.createRef<HTMLDivElement>();

    UNSAFE_componentWillMount(): void {
        const { categoriesDataView, getCategoriesParameter, loadCategories } = this.props;
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories(getCategoriesParameter);
        }
    }

    render() {
        const {
            categoriesDataView,
            topLevelCategoryIds,
            fields: { showImages, showOnlyTopLevelCategories, description, linkTitle, linkURL, title },
        } = this.props;
        if (categoriesDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!categoriesDataView.value || !topLevelCategoryIds) {
            return null;
        }

        const categories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);

        return (
            <StyledWrapper {...styles.mainContainer}>
                <StyledWrapper className="why-centro-intro" {...styles.containerWrapper}>
                    <StyledWrapper {...styles.titleContainer}>
                        {title && (
                            <Typography variant="h2" {...styles.title}>
                                {translate("{0}", title)}
                            </Typography>
                        )}
                        <Link href={linkURL} {...styles.readMoreLink}>
                            {parse(transformContentHtml(linkTitle), parserOptions)}
                            <Icon src={ArrowRightLongIcon} {...styles.rightIconStyles} />
                        </Link>
                    </StyledWrapper>
                    {description && (
                        <Typography variant="h5" {...styles.description}>
                            {translate("{0}", description)}
                        </Typography>
                    )}
                </StyledWrapper>
                <GridContainer {...styles.container}>
                    {categories.map(category => (
                        <GridItem key={category.id.toString()} {...styles.categoryItem}>
                            <GridContainer {...styles.innerContainer}>
                                {showImages && (
                                    <GridItem {...styles.categoryImageItem}>
                                        <StyledWrapper
                                            css={css`
                                                display: flex;
                                                flex-direction: column;
                                                width: 100%;
                                                align-items: initial;
                                            `}
                                        >
                                            {category.path && (
                                                <StyledWrapper {...styles.imageWrapper}>
                                                    <Clickable href={`${category.path}`} {...styles.categoryImageLink}>
                                                        <LazyImage
                                                            {...styles.categoryImage}
                                                            src={
                                                                category?.largeImagePath
                                                                    ? category?.largeImagePath
                                                                    : category?.smallImagePath
                                                            }
                                                            altText={category.imageAltText}
                                                        ></LazyImage>
                                                    </Clickable>
                                                </StyledWrapper>
                                            )}
                                            <StyledWrapper {...styles.categoryNameLinkItem}>
                                                <Link href={`${category.path}`}>
                                                    <Typography {...styles.categoryName}>
                                                        {category.shortDescription}
                                                    </Typography>
                                                </Link>
                                            </StyledWrapper>
                                            {!showOnlyTopLevelCategories && (
                                                <StyledWrapper {...styles.subCategoryWrapper}>
                                                    {category.subCategoryIds &&
                                                        category.subCategoryIds.slice(0, 4).map(subCategoryId => (
                                                            <GridItem
                                                                className="col"
                                                                key={subCategoryId}
                                                                {...styles.subCategoryNameLinkItem}
                                                            >
                                                                <SubCategoryLink categoryId={subCategoryId} />
                                                            </GridItem>
                                                        ))}
                                                    {category.subCategoryIds && category.subCategoryIds.length > 4 && (
                                                        <GridItem {...styles.viewMoreLinkItem}>
                                                            <Link href={category.path} {...styles.viewMoreLink}>
                                                                {translate("View More")}
                                                            </Link>
                                                        </GridItem>
                                                    )}
                                                </StyledWrapper>
                                            )}
                                        </StyledWrapper>
                                    </GridItem>
                                )}
                            </GridContainer>
                        </GridItem>
                    ))}
                </GridContainer>
            </StyledWrapper>
        );
    }
}

const SubCategoryLinkView = ({ category }: ReturnType<typeof linkMapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link href={category.path} {...styles.subCategoryNameLink}>
            {category.shortDescription}
        </Link>
    );
};

const linkMapStateToProps = (state: ApplicationState, ownProps: { categoryId: string }) => ({
    category: getCategoryState(state, ownProps.categoryId).value,
});

const SubCategoryLink = connect(linkMapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(HomepageCategoryList)),
    definition: {
        group: "Categories",
        icon: "list-ul",
        allowedContexts: ["CategoryListPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Title",
            },
            {
                fieldType: "Translatable",
                name: fields.description,
                editorTemplate: "TextField",
                displayName: "Introduction",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkTitle,
                editorTemplate: "TextField",
                displayName: "Link title",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.linkURL,
                editorTemplate: "TextField",
                displayName: "Link URL",
                defaultValue: "",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showOnlyTopLevelCategories,
                displayName: "Show only top-level categories",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
